import { tz } from '@date-fns/tz'
import { format as dateFormat, getSeconds } from 'date-fns'

import type { TodDisplayFormat } from './types'

type FormatTimeOfDayOptions = {
  timezone?: string
  format?: TodDisplayFormat | null
  seconds?: 'always' | 'nonzero' | 'never'
}

/**
 * Format the time of day with timezone and format.
 *
 * @param {Date} date - The date object to format
 * @param {string} [options.timezone ='UTC'] - The IANA timezone name, e.g., 'America/New_York'. Default is `'UTC'`
 * @param {string} [options.format ='24h'] - The time format, '12hNoAmPm' | '12h' | '24h' | null. Default is `'24h'`
 * @param {string} [options.seconds ='always'] - When to display seconds: 'always', 'nonzero', or 'never'. Default is
 *   `'always'`
 *
 * @returns {string} - The formatted time string
 */
export function formatTimeOfDay(
  date: Date,
  { timezone = 'UTC', format = '24h', seconds = 'always' }: FormatTimeOfDayOptions = {},
): string {
  // Keyed by the union rather than `string` so the lookup is total: adding a
  // TodDisplayFormat without a pattern here fails to compile instead of
  // silently formatting as undefined.
  const formatOptions: Record<TodDisplayFormat, string> = {
    '12hNoAmPm': 'h:mm', // -> 1:20
    '12h': 'h:mm a', // -> 1:20 PM
    '24h': 'HH:mm', // -> 13:00
  }

  // `||`, not `??`: the `= '24h'` default only covers undefined, and callers
  // pass null (stored setting unset) or '' (empty CSV/form field) too. Both
  // must land on 24h.
  let timeFormat = formatOptions[format || '24h']

  // Append seconds to the format string based on the seconds option
  if (seconds === 'always' || (seconds === 'nonzero' && getSeconds(date) !== 0)) {
    timeFormat = timeFormat.replace(':mm', ':mm:ss')
  }

  return dateFormat(date, timeFormat, { in: tz(timezone) })
}
